import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hook para columnas redimensionables en tablas implementadas como CSS grid.
 *
 * @param {string} storageKey - Clave única en localStorage para persistir anchos por usuario/tabla.
 * @param {number[]} defaultWidths - Array con los anchos por defecto en px.
 * @param {number} minWidth - Ancho mínimo permitido (px). Default 60.
 *
 * Retorna:
 *  - widths: array de anchos actuales en px
 *  - gridTemplate: string `${w1}px ${w2}px ...` listo para style.gridTemplateColumns
 *  - startResize(idx)(e): handler para mousedown sobre el divisor de la columna idx
 *  - resetWidths(): vuelve a los anchos por defecto y limpia localStorage
 *  - resizing: boolean (true mientras se está arrastrando)
 */
export default function useResizableColumns(storageKey, defaultWidths, minWidth = 60, flexCols = {}) {
  const [widths, setWidths] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length === defaultWidths.length) {
          return parsed.map((w, i) => Math.max(minWidth, Number(w) || defaultWidths[i]));
        }
      }
    } catch (_e) { /* ignore */ }
    return [...defaultWidths];
  });
  const [resizing, setResizing] = useState(false);
  const resizingRef = useRef(null); // { idx, startX, startW }

  // Persistir cambios en localStorage (debounced manual)
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch (_e) { /* quota or disabled */ }
  }, [storageKey, widths]);

  const handleMove = useCallback((e) => {
    if (!resizingRef.current) return;
    const { idx, startX, startW } = resizingRef.current;
    const dx = e.clientX - startX;
    setWidths((prev) => {
      const next = [...prev];
      next[idx] = Math.max(minWidth, startW + dx);
      return next;
    });
  }, [minWidth]);

  const handleUp = useCallback(() => {
    resizingRef.current = null;
    setResizing(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    if (!resizing) return;
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [resizing, handleMove, handleUp]);

  const startResize = useCallback((idx) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { idx, startX: e.clientX, startW: widths[idx] };
    setResizing(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [widths]);

  const resetWidths = useCallback(() => {
    setWidths([...defaultWidths]);
    try { localStorage.removeItem(storageKey); } catch (_e) { /* ignore */ }
  }, [defaultWidths, storageKey]);

  // flexCols: {colIdx: fr} — esas columnas crecen para llenar el ancho disponible
  // en pantallas grandes (el ancho guardado actúa como mínimo).
  const gridTemplate = widths
    .map((w, i) => (flexCols[i] ? `minmax(${w}px, ${flexCols[i]}fr)` : `${w}px`))
    .join(" ");

  return { widths, gridTemplate, startResize, resetWidths, resizing };
}
